/**
 * Dealer-book maths for the GEX ladder (plan §13 DEALER RAIL).
 *
 * Framework-agnostic on purpose — no Svelte, no DOM, no fetch. INTELCHAT's surviving rider on the
 * chart ruling is "keep the chart core's public surface clean enough that the shell never leaks into
 * it"; the same discipline applies here, and it is what makes these numbers testable without a browser.
 *
 * EVERY NUMERIC FIELD FROM UW ARRIVES AS A STRING (`"put_gex": "-0.0011"`) — see
 * docs/design/PANEL_DATA_CONTRACTS_2026-08-06.md. Coercion happens once, here, at the boundary.
 * Nothing downstream parses a float out of a string in a render path.
 *
 * NOTHING IN THIS FILE IS CALIBRATED. Flip, walls and KING are HYPOTHESIS until E1/E2/E4 rule
 * (law 6). The panel labels them as such; this module only computes them.
 */
import { lastSessionFreshness, type LastSessionFreshness, type ServedVenueClock } from "./order33State.ts";

/** One row exactly as `/api/stock/<root>/greek-exposure/strike` lands it. */
export interface GexStrikeRow {
  date: string;
  strike: string;
  put_gex: string;
  call_gex: string;
  put_charm?: string;
  put_delta?: string;
  put_vanna?: string;
  call_charm?: string;
  call_delta?: string;
  call_vanna?: string;
}

export interface StrikeGex {
  strike: number;
  /** call_gex + put_gex. Put gamma arrives already signed negative. */
  net: number;
  call: number;
  put: number;
}

export type Regime = "POS_GAMMA" | "NEG_GAMMA" | "FLAT";

export interface DealerBook {
  /** Ascending by strike. */
  strikes: StrikeGex[];
  netTotal: number;
  regime: Regime;
  /** Strike where the cumulative net-GEX curve changes sign. null when it never crosses. */
  flip: number | null;
  /** Largest positive net-GEX strike above spot. */
  callWall: number | null;
  /** Largest negative net-GEX strike below spot (most negative). */
  putWall: number | null;
  /** Largest |net| anywhere — the max-γ strike. */
  king: number | null;
  /** Share of total |net GEX| sitting at `king`. The mock calls this "γ-share". */
  kingShare: number;
}

export type DealerWindowCenterName =
  | "SPOT"
  | "SNAPSHOT UNDERLYING"
  | "LAST SERVED SPOT"
  | "KING";

export interface DealerWindowCenter {
  price: number;
  name: DealerWindowCenterName;
}

export interface DealerWindowFallbacks {
  snapshotUnderlying?: number | null;
  lastServedSpot?: number | null;
  king?: number | null;
}

function usablePrice(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

/** Resolve a window centre without turning an absent price into zero or list position. */
export function resolveDealerWindowCenter(
  spot: number | null | undefined,
  fallbacks: DealerWindowFallbacks = {},
): DealerWindowCenter | null {
  const candidates: Array<[DealerWindowCenterName, number | null | undefined]> = [
    ["SPOT", spot],
    ["SNAPSHOT UNDERLYING", fallbacks.snapshotUnderlying],
    ["LAST SERVED SPOT", fallbacks.lastServedSpot],
    ["KING", fallbacks.king],
  ];
  for (const [name, price] of candidates) {
    if (usablePrice(price)) return { name, price };
  }
  return null;
}

/**
 * A number that is not a number is a FAULT, not a zero. Coercing `"abc"` or `null` to 0 would sink a
 * bad row silently into the middle of a gamma profile — the fail-soft class this repo refuses.
 */
function num(v: string | number | undefined | null, field: string): number {
  // f54430c6 / R3190 (Astra, CONFIRMED): Number("") and Number(null) are 0, which PASSES the finite
  // check below - a blank or absent gamma became a real zero and flipped the regime. Absence is refused
  // by name BEFORE coercion.
  if (v === null || v === undefined || (typeof v === "string" && v.trim() === "") || (typeof v !== "string" && typeof v !== "number")) {
    throw new TypeError(`dealer: ${field} is absent or blank: ${JSON.stringify(v)}`);
  }
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) throw new TypeError(`dealer: ${field} is not numeric: ${JSON.stringify(v)}`);
  return n;
}

export function parseStrikes(rows: GexStrikeRow[]): StrikeGex[] {
  return rows
    .map((r) => {
      const call = num(r.call_gex, "call_gex");
      const put = num(r.put_gex, "put_gex");
      return { strike: num(r.strike, "strike"), call, put, net: call + put };
    })
    .sort((a, b) => a.strike - b.strike);
}

/**
 * How small a number must be, RELATIVE TO THE BOOK'S OWN SIZE, before it may not claim a direction.
 *
 * ⛔ ONE FLOOR, TWO READERS. `buildBook`'s regime and `gammaFlip` are the same question asked twice
 * — "is this net meaningfully signed?" — and a second constant for one instrument is how the two
 * answers drift apart. It is relative, so it means the same thing on a quiet chain as on a huge one.
 */
export const NEGLIGIBLE_SHARE = 1e-6;

/**
 * The gamma flip: where cumulative dealer gamma from the bottom of the chain crosses zero.
 *
 * Returns the strike at which the crossing happens, or null if the curve never changes sign. A
 * whole chain of one sign is a real market state (deeply positive gamma), not a missing value —
 * so it returns null and the panel renders "no flip in chain" rather than inventing a level.
 *
 * ⛔⛔ THE NEGLIGIBILITY FLOOR IS NOT DEFENSIVE — IT IS THIS REPO'S OWN LAW, AND THIS WAS THE ONE
 * PLACE IT WAS NOT APPLIED. CC-ss traced the owner's `flip −7245` chip (`bh=c1c10621fb`) and RAN the
 * algorithm on live data rather than reading it:
 * ```
 *   /api/dealer/gex  spot 7645 · 807 strikes · range 200 → 20000
 *     strike  200 .. net ** +0.0002 **  ⇒ bare Math.sign() sets prevSign := +1
 *     strike  400 .. net ** -0.0701 **  ⇒ cumulative turns negative ⇒ CROSSING ⇒ 400
 *   chip = flip − spot = 400 − 7645 = ** −7245 **   ⇒ the owner's number, exactly
 * ```
 * ⇒ ***THE ENTIRE GAMMA FLIP WAS PINNED BY A NET WORTH 3.08e-14 OF THE BOOK*** — the deepest, most
 * illiquid strike in an 807-strike chain, on my own reproduction. The chip was never lying: it
 * renders a DISTANCE and says so. **The label was honest and the strike it measured to was wrong.**
 *
 * 🔑 `buildBook` already returns FLAT when the net is negligible against `absTotal`, and TopStrip
 * pins the reason — ***"zero may not claim a direction"*** (the `▲ 0.00` defect). This function used
 * bare `Math.sign()`, so a value 350× smaller than its neighbour set the sign that every later
 * comparison was made against. ⇒ ***A LAW APPLIED IN ONE READER OF A QUANTITY AND NOT THE OTHER IS
 * NOT A LAW, IT IS A HABIT*** — and the unguarded reader is the one that renders to the owner.
 *
 * ⚠ THE FLOOR IS APPLIED TO THE RUNNING CUMULATIVE, NOT TO EACH STRIKE. A crossing is a statement
 * about where the CURVE is, and near a genuine crossing the cumulative is legitimately near zero —
 * so the flip is reported at the strike where cumulative gamma first becomes MEANINGFULLY opposite,
 * never where it merely grazes zero.
 */
export function gammaFlip(strikes: StrikeGex[]): number | null {
  // The book's own scale — the same denominator `buildBook` divides by, so one chain gets one answer.
  const absTotal = strikes.reduce((a, s) => a + Math.abs(s.net), 0);
  if (absTotal === 0) return null; // a chain of nothing has no direction to claim
  const floor = absTotal * NEGLIGIBLE_SHARE;
  let cum = 0;
  let prevSign = 0;
  for (const s of strikes) {
    const next = cum + s.net;
    const sign = Math.abs(next) < floor ? 0 : Math.sign(next);
    if (prevSign !== 0 && sign !== 0 && sign !== prevSign) return s.strike;
    if (sign !== 0) prevSign = sign;
    cum = next;
  }
  return null;
}

/**
 * Walls are read RELATIVE TO SPOT, not as global extremes: a call wall below spot is not a ceiling.
 * Without a spot price there is no above/below, so both come back null rather than guessed.
 */
export function walls(strikes: StrikeGex[], spot: number | null): { callWall: number | null; putWall: number | null } {
  if (spot === null) return { callWall: null, putWall: null };
  let callWall: StrikeGex | null = null;
  let putWall: StrikeGex | null = null;
  for (const s of strikes) {
    if (s.strike > spot && s.net > 0 && (!callWall || s.net > callWall.net)) callWall = s;
    if (s.strike < spot && s.net < 0 && (!putWall || s.net < putWall.net)) putWall = s;
  }
  return { callWall: callWall?.strike ?? null, putWall: putWall?.strike ?? null };
}

export function buildBook(rows: GexStrikeRow[], spot: number | null): DealerBook {
  return buildBookFromStrikes(parseStrikes(rows), spot);
}

/**
 * Build the same scale-invariant level geometry from an already-admitted exposure chain.
 * The caller retains responsibility for naming the quantity (GEX versus gamma-OI); this helper
 * deliberately does not relabel it. Rows are copied and sorted so the cumulative flip is stable.
 */
export function buildBookFromStrikes(rows: readonly StrikeGex[], spot: number | null): DealerBook {
  const strikes = [...rows].sort((a, b) => a.strike - b.strike);
  const netTotal = strikes.reduce((a, s) => a + s.net, 0);
  const absTotal = strikes.reduce((a, s) => a + Math.abs(s.net), 0);

  let king: StrikeGex | null = null;
  for (const s of strikes) if (!king || Math.abs(s.net) > Math.abs(king.net)) king = s;

  return {
    strikes,
    netTotal,
    // A dead-flat book is FLAT, not arbitrarily positive. The threshold is relative to the book's
    // own size, so it means the same thing on a quiet chain as on a huge one.
    regime: absTotal === 0 || Math.abs(netTotal) < absTotal * NEGLIGIBLE_SHARE ? "FLAT" : netTotal > 0 ? "POS_GAMMA" : "NEG_GAMMA",
    flip: gammaFlip(strikes),
    ...walls(strikes, spot),
    king: king?.strike ?? null,
    kingShare: king && absTotal > 0 ? Math.abs(king.net) / absTotal : 0,
  };
}

/**
 * The ladder shows a WINDOW, never 787 rows: at one row per strike the bars near the selected
 * centre would be two pixels tall and unreadable. Returns the `count` strikes closest to centre,
 * ascending; the centre is live spot or a named fallback when spot is absent.
 * The caller renders how many were withheld — a silent truncation reads as "this is the whole book".
 */
export function windowAroundSpot(
  strikes: StrikeGex[],
  spot: number | null,
  count: number,
  mustInclude: Array<number | null> = [],
  fallbacks: DealerWindowFallbacks = {},
): StrikeGex[] {
  if (strikes.length <= count) return strikes;
  const center = resolveDealerWindowCenter(spot, fallbacks);
  if (center === null) {
    throw new RangeError(
      "dealer window: spot absent and no snapshot underlying, last served spot, or KING centre is available",
    );
  }
  const closest = [...strikes]
    .sort((a, b) => Math.abs(a.strike - center.price) - Math.abs(b.strike - center.price))
    .slice(0, count);

  // ORDER 2: widen symmetrically around spot until the NEARER wall is inside. Supplying both walls
  // is intentional; this function chooses the nearer one so a distant tail cannot flatten the
  // readable book. The original count remains the minimum, never a cap that hides the level.
  const nearer = mustInclude
    .filter((price): price is number => typeof price === "number" && Number.isFinite(price))
    .sort((a, b) => Math.abs(a - center.price) - Math.abs(b - center.price))[0];
  if (nearer === undefined) return closest.sort((a, b) => a.strike - b.strike);
  const baseRadius = closest.reduce(
    (radius, row) => Math.max(radius, Math.abs(row.strike - center.price)),
    0,
  );
  const radius = Math.max(baseRadius, Math.abs(nearer - center.price));
  return strikes
    .filter((row) => Math.abs(row.strike - center.price) <= radius)
    .sort((a, b) => a.strike - b.strike);
}

// =============================================================================
// THE SPX → ES BASIS, AND THE LEVELS THAT CROSS THE PRICE CHART
//
// The dealer book is quoted in SPX CASH. The price chart's axis is ES1!. They are not the same
// number and the gap is not small: measured 2026-08-09 the basis was +22.25 points. Drawing a 7900
// strike at 7900 on an ES axis is off by a whole trading range.
//
// ⚠ THE MOCK'S SUBTITLE SAYS `ratio 0.9983`. THAT CONSTANT IS MOCK FURNITURE. Measured live the
// ratio was 0.997139, and using 0.9983 places EVERY level ~9 points too low — see
// docs/design/LEVELS_OVERLAY_DESIGN_BOARD_2026-08-09.md for the table. The basis moves with rates,
// dividends and time to expiry, so no constant is right for long. It is derived per poll and it is
// printed on screen as a measured value.
// =============================================================================

/** A dealer level after conversion, ready to draw. `spx` is kept so the label can cite the strike. */
export interface DealerLevel {
  /** "GAMMA FLIP" · "CALL WALL" · "PUT WALL" · "KING · γ-share 5.0%", joined on a shared price. */
  name: string;
  /** The level in SPX cash points — the number the dealer feed actually gave us. */
  spx: number;
  /** The same level on the ES axis, via the measured basis. */
  es: number;
  /** Which encoding class draws it: magnets solid in --magnet, the flip dashed in --flip. */
  cls: "magnet" | "gammaflip";
  /** The pre-registered study that would move this level off HYPOTHESIS (law 6). */
  study: string;
}

/**
 * ES points per SPX point, measured from the two live payloads.
 *
 * Returns null rather than 1 when either input is missing or non-positive. A ratio of 1 is a
 * CLAIM — that ES and SPX trade at parity — and it is a claim we would be making out of a missing
 * measurement. The caller renders the absence; it never silently draws at parity.
 */
export function basisRatio(spxSpot: number | null | undefined, esLast: number | null | undefined): number | null {
  if (typeof spxSpot !== "number" || typeof esLast !== "number") return null;
  if (!Number.isFinite(spxSpot) || !Number.isFinite(esLast) || spxSpot <= 0 || esLast <= 0) return null;
  return esLast / spxSpot;
}

/** An SPX cash level expressed on the ES axis. */
export function toEsPrice(spxLevel: number, ratio: number): number {
  return spxLevel * ratio;
}

/**
 * The four levels the overlay exists to draw, converted and merged.
 *
 * Merging is not cosmetic: KING and the CALL WALL sit on the SAME STRIKE whenever the biggest
 * absolute gamma in the book is also the biggest positive one above spot — which is the live state
 * today (both 7900). Two lines at one price is one mark with two names, and stacking two labels at
 * one y makes both unreadable. `DealerRailPanel` already merges on exactly this rule; this is that
 * rule moved somewhere both panels can reach it.
 *
 * A null level is DROPPED, not drawn at zero. `gammaFlip` returns null for a chain that never
 * changes sign, and walls return null without a spot — both are real states, and inventing a price
 * for them would be the fail-soft class this repo refuses.
 */
/**
 * TODAY'S 0DTE LEVELS → the shape the chart already draws. R410③'s repoint, in one function.
 *
 * ⛔ THE CHART'S WALLS COME FROM HERE NOW, NOT FROM `dealerLevels()`. Same output type on purpose:
 * `ChartCanvas` already renders off-frame EDGE TAGS, the `{on,off}` count and the `HYP·<study>`
 * label for `DealerLevel[]`, and every one of those properties is exactly what 0DTE walls need. The
 * repoint is a SOURCE change, not a render change — reuse beats build, and the reused half is the
 * half that took three seats to get right.
 *
 * ⚠ SPX → ES: the route serves SPX strikes; the chart is ES. Converted through the SAME measured
 * `ratio` the pooled path uses, so both level sets sit on one basis and a reader comparing the chart
 * to §STRUCTURE is comparing like with like.
 *
 * ⛔ A NULL LEVEL IS DROPPED, NEVER DRAWN AT ZERO, and its absence is reported by
 * `absentLevelNotesFromLevels()` — silence about a level that does not exist is the defect this
 * whole build exists to kill.
 */
export function zeroDteLevels(
  lv: { call_wall: number | null; put_wall: number | null; gamma_flip: number | null },
  ratio: number,
): DealerLevel[] {
  const raw: Array<{ name: string; price: number | null; cls: DealerLevel["cls"]; study: string }> = [
    { name: "GAMMA FLIP", price: lv.gamma_flip, cls: "gammaflip", study: "E1" },
    { name: "CALL WALL", price: lv.call_wall, cls: "magnet", study: "E4" },
    { name: "PUT WALL", price: lv.put_wall, cls: "magnet", study: "E4" },
  ];

  const out: DealerLevel[] = [];
  for (const l of raw) {
    if (l.price === null) continue;
    const hit = out.find((o) => o.spx === l.price);
    if (hit) {
      // " + ", never " · " — the renderer splits labels on " · " to drop whole FACTS when the
      // canvas is narrow, so a name containing it loses the price. Same contract as dealerLevels().
      hit.name = `${hit.name} + ${l.name}`;
      if (!hit.study.split("/").includes(l.study)) hit.study = `${hit.study}/${l.study}`;
      if (l.cls === "gammaflip") hit.cls = "gammaflip";
    } else {
      out.push({ name: l.name, spx: l.price, es: toEsPrice(l.price, ratio), cls: l.cls, study: l.study });
    }
  }
  return out.sort((a, b) => b.es - a.es);
}

/** `absentLevelNotes` for the 0DTE payload — same clauses, same order, one vocabulary. */
export function absentLevelNotesFromLevels(
  lv: { call_wall: number | null; put_wall: number | null; gamma_flip: number | null },
): string[] {
  return absentLevelNotes({ flip: lv.gamma_flip, callWall: lv.call_wall, putWall: lv.put_wall } as DealerBook);
}

/**
 * WHICH RULED LEVELS THE BOOK DOES NOT HAVE — a REAL market state, not a fault.
 *
 * ⛔ WHY THIS EXISTS, MEASURED ON TODAY'S 0DTE (2026-08-21): `walls()` returns 7700/7590 and
 * `gammaFlip()` returns **null** — the cumulative net never crosses zero at this expiry. The panel's
 * `levelsBlockedReason` only fires when NOTHING draws, so with two walls present the flip's absence
 * was SILENT, and the legend went on advertising a "regime boundary — gamma flip" series that is not
 * on the chart.
 * ⇒ ***A LEGEND THAT NAMES A SERIES THE CHART DOES NOT DRAW IS WORSE THAN SILENCE: it is an
 * assertion.*** And a PARTIAL absence is the case the all-or-nothing guard cannot see.
 * ⇒ R410's law, verbatim: **flip=null renders as "no flip in range", NEVER a number.** The pooled
 * all-expiry ladder happily supplies 8800 here; taking it would be the exact lie this build kills.
 *
 * Returns the human clause for each absent class, ordered as the legend reads. Empty = nothing absent.
 */
export function absentLevelNotes(book: DealerBook): string[] {
  const out: string[] = [];
  // The flip is the one a reader will actively look for, so it is named first and by its own words.
  if (book.flip === null) out.push("no flip in range");
  if (book.callWall === null && book.putWall === null) out.push("no walls in range");
  else if (book.callWall === null) out.push("no call wall in range");
  else if (book.putWall === null) out.push("no put wall in range");
  return out;
}

export function dealerLevels(book: DealerBook, ratio: number): DealerLevel[] {
  const raw: Array<{ name: string; price: number | null; cls: DealerLevel["cls"]; study: string }> = [
    { name: "GAMMA FLIP", price: book.flip, cls: "gammaflip", study: "E1" },
    { name: "CALL WALL", price: book.callWall, cls: "magnet", study: "E4" },
    { name: "PUT WALL", price: book.putWall, cls: "magnet", study: "E4" },
    // γ-share is deliberately NOT in the chart's label. The dealer rail beside this chart already
    // carries it, and on a level line the share is the one number you cannot act on — the price and
    // the distance are. One fact, one place.
    { name: "KING", price: book.king, cls: "magnet", study: "E2" },
  ];

  const out: DealerLevel[] = [];
  for (const l of raw) {
    if (l.price === null) continue;
    const hit = out.find((o) => o.spx === l.price);
    if (hit) {
      // ⚠ JOINED WITH " + ", NOT " · ", AND THE REASON IS NOT TYPOGRAPHIC. The chart renderer
      // splits a label on " · " to drop whole facts when the canvas is too narrow (fitLabel), so a
      // name containing " · " is read as two facts and the PRICE — which encoding rule 1 makes
      // mandatory — gets dropped as if it were the middle of a list. Caught on a 390px viewport,
      // where "CALL WALL · KING 7909.93 · +145 pts · HYP·E4/E2" degraded to "CALL WALL · HYP·E4/E2".
      // "+" also reads better for what it means: two levels sitting on ONE strike.
      hit.name = `${hit.name} + ${l.name}`;
      // A merged pair keeps both studies: the line is HYPOTHESIS until BOTH have ruled, and
      // showing only the first would understate what is still unproven.
      if (!hit.study.split("/").includes(l.study)) hit.study = `${hit.study}/${l.study}`;
      // The regime boundary wins the class when it coincides with a magnet — a dashed line that is
      // also a wall still has to read as the boundary, which is the rarer and more consequential fact.
      if (l.cls === "gammaflip") hit.cls = "gammaflip";
    } else {
      out.push({ name: l.name, spx: l.price, es: toEsPrice(l.price, ratio), cls: l.cls, study: l.study });
    }
  }
  return out.sort((a, b) => b.es - a.es);
}

/**
 * DEALER LEVELS -> CHART LEVELS, ONE IMPLEMENTATION FOR BOTH CHARTS (R814).
 *
 * This lived as a `$derived` inside `EsChartPanel`, which is why the TradingView charts had no
 * levels: the mapping was not reachable from the other renderer. Copying it would have been a
 * second implementation of a label format, a distance rule and a study chip — three things that
 * must not drift between two charts the owner reads side by side.
 * ⇒ ***A MAPPING THAT LIVES INSIDE ONE COMPONENT IS A MAPPING THE NEXT COMPONENT WILL RE-INVENT.***
 *
 * ⛔ `px` IS THE ES PRICE, ALWAYS. The SPX number never reaches a chart: the basis is ~15 points and
 * a mis-mapped line looks entirely plausible, which is precisely why it prompts no investigation.
 * ⚠ `score`/`effectivePx` are required by `ScoredLevel` and meaningless here — this path deliberately
 * does NOT route through `selectLevels`, whose proximity window DROPS far levels. A dropped dealer
 * level is the silent omission the overlay exists to remove; the renderer decides line vs edge tag.
 */
export function chartLevelsFromDealer(
  levels: DealerLevel[],
  esLast: number | null,
  scopeToken = "0DTE ES",
): import("./chart/types/index.ts").ScoredLevel[] {
  /**
   * Two decimals inside 10 points, whole points beyond: the digits matter when price is sitting on
   * a level and are noise when it is 390 points away. An empty string when there is no last price —
   * a distance from nothing is not a distance.
   */
  const distance = (es: number): string => {
    if (esLast === null || !Number.isFinite(esLast)) return "";
    const d = es - esLast;
    const mag = Math.abs(d);
    return ` · ${d >= 0 ? "+" : "−"}${mag < 10 ? mag.toFixed(2) : mag.toFixed(0)} pts`;
  };
  return levels.map((l) => ({
    type: l.cls,
    px: l.es,
    // NAME · ES PRICE · DISTANCE · pending STUDY. Four facts is the ceiling for a label read at a
    // glance, and the SPX strike is deliberately absent: the header prints the measured ratio, so
    // the rail's SPX numbers reconcile without a second set of digits on every line.
    label: `${l.name} ${l.es.toFixed(2)}` + distance(l.es) + ` · ${scopeToken} · HYP·${l.study}`,
    score: 1,
    effectivePx: l.es,
  }));
}

/** UW source=vol flip supplied by both dealer doors; omitted on older deployments. */
export interface VendorFlip {
  value: number | null;
  source: "vol";
  root: "SPX";
  market_date: string;
  vendor_ts: string | null;
  receipt_ts: string | null;
  poll_seq: string | number | null;
  age_s: number | null;
  state: "LIVE" | "STALE" | "ABSENT";
}

// =============================================================================
// HUB-LEVEL-SET-1 (ORDER_HUD_LEVEL_SET_1 r1.1; D1-D3 (a), R4226): ONE SET OF LEVELS PER CHART.
//
// The pill picks the 0DTE set (today's walls from levels0dte, the flip per D3 (a)) or the UW vol set
// (the vendor's four levels, served by /api/dealer/levels as vendor_levels). ONE set is drawn at a
// time. The vendor set is named by its SOURCE because its expiry scope is unmeasured (D1 (a)).
// ⛔ THE FLIP IS THE VENDOR'S VOL FLIP IN BOTH SETS (R4082: one flip object on the face). R410's
// crossing never draws here; it lives behind the glyph. Its name carries "UW vol" in both sets
// (R4226), so a 0DTE pill never implies a 0DTE flip. The suffix joins with a SPACE: " · " is the
// renderer's fact separator and must not appear inside a name (ChartCanvas fitLabel).
// =============================================================================

/** D1 (a): the pill's two labels. */
export type LevelSetName = "0DTE" | "UW vol";
export const LEVEL_SET_NAMES: readonly LevelSetName[] = ["0DTE", "UW vol"];

export interface VendorLevels {
  put_wall: VendorFlip;
  call_wall: VendorFlip;
  gamma_flip: VendorFlip;
  gamma_magnet: VendorFlip;
  source: "vol";
}

/** The /api/dealer/levels body as the level set reads it. The vendor fields are absent on older workers. */
export interface LevelSetBody {
  call_wall: number | null;
  put_wall: number | null;
  /** R410's crossing: behind the glyph only. */
  gamma_flip: number | null;
  vendor_flip?: VendorFlip;
  vendor_flip_refused?: string;
  vendor_levels?: VendorLevels;
  vendor_levels_refused?: string;
  /** The walls' own window: the door's verdict and age (the 0DTE set draws them). */
  stale?: boolean | null;
  age_s?: number | null;
  /** The 0DTE walls' session date and the door's own venue clock (ORDER_HUD_GEX_GRID_CLOSED_1 D4). */
  expiry?: string | null;
  venue_state?: ServedVenueClock | null;
}

const VENDOR_FLIP_NAME = "GAMMA FLIP UW vol";

function mergedLevels(
  raw: Array<{ name: string; price: number | null; cls: DealerLevel["cls"]; study: string }>,
  ratio: number,
): DealerLevel[] {
  const out: DealerLevel[] = [];
  for (const l of raw) {
    if (l.price === null) continue;
    const hit = out.find((o) => o.spx === l.price);
    if (hit) {
      hit.name = `${hit.name} + ${l.name}`;
      if (!hit.study.split("/").includes(l.study)) hit.study = `${hit.study}/${l.study}`;
      if (l.cls === "gammaflip") hit.cls = "gammaflip";
    } else {
      out.push({ name: l.name, spx: l.price, es: toEsPrice(l.price, ratio), cls: l.cls, study: l.study });
    }
  }
  return out.sort((a, b) => b.es - a.es);
}

/** The vendor's value, or null for ABSENT. A non-numeric value is a broken door, never a line at zero. */
function vendorValue(level: VendorFlip | undefined): number | null {
  if (!level || level.state === "ABSENT" || level.value === null) return null;
  return typeof level.value === "number" && Number.isFinite(level.value) ? level.value : null;
}

/**
 * The lines ONE chart draws for the chosen set, and the clauses the glyph names for what is not drawn.
 * Every line reaches ES through the same measured ratio as the rest of the chart.
 */
export function chartLevelSet(set: LevelSetName, body: LevelSetBody, ratio: number): { lines: DealerLevel[]; absent: string[] } {
  const absent: string[] = [];
  if (set === "UW vol") {
    const v = body.vendor_levels;
    if (!v) {
      absent.push(body.vendor_levels_refused ? `UW vol UNSERVED · ${body.vendor_levels_refused}` : "UW vol UNSERVED");
      return { lines: [], absent };
    }
    const raw = [
      { name: VENDOR_FLIP_NAME, label: "GAMMA FLIP", price: vendorValue(v.gamma_flip), cls: "gammaflip" as const, study: "E1" },
      { name: "CALL WALL", label: "CALL WALL", price: vendorValue(v.call_wall), cls: "magnet" as const, study: "E4" },
      { name: "PUT WALL", label: "PUT WALL", price: vendorValue(v.put_wall), cls: "magnet" as const, study: "E4" },
      { name: "MAGNET", label: "MAGNET", price: vendorValue(v.gamma_magnet), cls: "magnet" as const, study: "E2" },
    ];
    for (const l of raw) if (l.price === null) absent.push(`${l.label} ABSENT · UW vol`);
    return { lines: mergedLevels(raw, ratio), absent };
  }
  const flip = vendorValue(body.vendor_flip);
  if (flip === null) {
    absent.push(body.vendor_flip
      ? "GAMMA FLIP ABSENT · UW vol"
      : `GAMMA FLIP UNSERVED${body.vendor_flip_refused ? ` · ${body.vendor_flip_refused}` : ""} · UW vol`);
  }
  if (body.call_wall === null && body.put_wall === null) absent.push("no walls in range");
  else if (body.call_wall === null) absent.push("no call wall in range");
  else if (body.put_wall === null) absent.push("no put wall in range");
  const lines = mergedLevels([
    { name: VENDOR_FLIP_NAME, price: flip, cls: "gammaflip", study: "E1" },
    { name: "CALL WALL", price: body.call_wall, cls: "magnet", study: "E4" },
    { name: "PUT WALL", price: body.put_wall, cls: "magnet", study: "E4" },
  ], ratio);
  return { lines, absent };
}

/** L2: the choice persists per chart beside the VWAP prefs, under its own key. The default is the 0DTE set. */
export const LEVEL_SET_PREFS_KEY = "spx-hub.level-set.v1";
type PrefStorage = Pick<Storage, "getItem" | "setItem">;

function levelSetStorage(): PrefStorage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function readLevelSetPrefs(storage: PrefStorage | null): { stored: Record<string, unknown>; error: string | null } {
  if (!storage) return { stored: {}, error: "preference storage unavailable; the level set will not persist" };
  try {
    const parsed: unknown = JSON.parse(storage.getItem(LEVEL_SET_PREFS_KEY) ?? "{}");
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return { stored: {}, error: "stored level set preferences have an invalid shape" };
    }
    return { stored: parsed as Record<string, unknown>, error: null };
  } catch (error) {
    return { stored: {}, error: `preference read failed — ${error instanceof Error ? error.message : String(error)}` };
  }
}

export function storedLevelSet(panelId: string, storage = levelSetStorage()): { set: LevelSetName; error: string | null } {
  const read = readLevelSetPrefs(storage);
  const value = read.stored[panelId];
  const invalid = value !== undefined && !LEVEL_SET_NAMES.includes(value as LevelSetName);
  const error = read.error ?? (invalid ? `stored level set for ${panelId} is not a level set` : null);
  if (error !== null) console.error(`[level-set] ${error}`);
  return { set: invalid || value === undefined ? "0DTE" : value as LevelSetName, error };
}

export function rememberLevelSet(panelId: string, set: LevelSetName, storage = levelSetStorage()): string | null {
  const read = readLevelSetPrefs(storage);
  if (!storage) {
    console.error(`[level-set] ${read.error}`);
    return read.error;
  }
  try {
    storage.setItem(LEVEL_SET_PREFS_KEY, JSON.stringify({ ...read.stored, [panelId]: set }));
    return null;
  } catch (error) {
    const message = `preference write failed — ${error instanceof Error ? error.message : String(error)}`;
    console.error(`[level-set] ${message}`);
    return message;
  }
}

/**
 * The oldest age among the set's drawn levels when any is STALE (the pill wears it), else null.
 * The 0DTE set draws the door's walls too, so their own verdict counts beside the vendor flip's.
 */
/** Every stale read of the set with its age and its freshness beside the door's own venue clock (D4 (a)). */
function levelSetStaleReads(set: LevelSetName, body: LevelSetBody): { ageS: number; freshness: LastSessionFreshness }[] {
  const levels = set === "UW vol"
    ? (body.vendor_levels ? [body.vendor_levels.put_wall, body.vendor_levels.call_wall,
      body.vendor_levels.gamma_flip, body.vendor_levels.gamma_magnet] : [])
    : (body.vendor_flip ? [body.vendor_flip] : []);
  const reads: { ageS: number; date: string | null }[] = levels.filter((l) => l.state === "STALE" && typeof l.age_s === "number")
    .map((l) => ({ ageS: l.age_s as number, date: l.market_date ?? null }));
  if (set === "0DTE" && body.stale === true && typeof body.age_s === "number") reads.push({ ageS: body.age_s, date: body.expiry ?? null });
  return reads.map((r) => ({ ageS: r.ageS, freshness: lastSessionFreshness(true, body.venue_state, [r.date]) }));
}

/** The oldest read that stays STALE; a last-session read at a CLOSED venue is CLOSED, never counted here. */
export function levelSetStaleAge(set: LevelSetName, body: LevelSetBody): number | null {
  const ages = levelSetStaleReads(set, body).filter((r) => r.freshness === "STALE").map((r) => r.ageS);
  return ages.length ? Math.max(...ages) : null;
}

/**
 * A vendor read the set depends on that the door did not serve (refused by name, or an older worker), worn on the
 * pill: "UNSERVED" when the UW vol set has no levels, "FLIP UNSERVED" when the 0DTE set has no flip. ABSENT is a
 * market state and stays behind the glyph.
 */
export function levelSetUnserved(set: LevelSetName, body: LevelSetBody): string | null {
  if (set === "UW vol") return body.vendor_levels ? null : "UNSERVED";
  return body.vendor_flip ? null : "FLIP UNSERVED";
}

/** A vendor level's own as-of, dated (a CLOSED body may serve a prior session): "receipt 2026-10-02 19:58:00Z". */
function vendorAsOf(level: VendorFlip): string {
  const iso = level.receipt_ts ?? "";
  return `UW vol receipt ${iso.slice(0, 10)} ${iso.slice(11, 19)}Z${level.state === "STALE" ? " · STALE" : ""}`;
}

/**
 * L3 + L4: the glyph's lines for the chosen set. Each drawn level names both prices and ITS OWN source and as-of
 * (codex review 01a10cc3-c1d3: the vendor picks each level's newest row alone, so one receipt never speaks for the
 * set). R410's crossing is named here and never drawn.
 */
export function levelSetGlyphNotes(set: LevelSetName, body: LevelSetBody, ratio: number): string[] {
  const price = (spx: number) => `SPX ${spx.toFixed(2)} · ES ${toEsPrice(spx, ratio).toFixed(2)}`;
  const notes: string[] = [];
  const vendor = (label: string, level: VendorFlip | undefined) => {
    const value = vendorValue(level);
    if (level && value !== null) notes.push(`${label} · ${price(value)} · ${vendorAsOf(level)}`);
  };
  if (set === "UW vol") {
    const v = body.vendor_levels;
    vendor("GAMMA FLIP", v?.gamma_flip);
    vendor("CALL WALL", v?.call_wall);
    vendor("PUT WALL", v?.put_wall);
    vendor("MAGNET", v?.gamma_magnet);
  } else {
    vendor("GAMMA FLIP", body.vendor_flip);
    if (body.call_wall !== null) notes.push(`CALL WALL · ${price(body.call_wall)} · 0DTE door`);
    if (body.put_wall !== null) notes.push(`PUT WALL · ${price(body.put_wall)} · 0DTE door`);
  }
  notes.push(body.gamma_flip === null
    ? "R410 0DTE crossing · none in range"
    : `R410 0DTE crossing · SPX ${body.gamma_flip.toFixed(2)} · not drawn`);
  return notes;
}

/**
 * The active pill's note: EVERY condition at once, never one hiding another (codex review 01a10cbf-7063): an unserved
 * vendor read, the drawn set's stale age, and a failed preference read or write (the alert lane is the three door
 * alerts', so the level set's own storage failure is worn on its own pill). null when there is nothing to say.
 */
export function levelSetPillNote(
  set: LevelSetName, body: LevelSetBody | null, formatAgeS: (s: number) => string, prefsError: string | null,
): string | null {
  const parts: string[] = [];
  if (body) {
    const unserved = levelSetUnserved(set, body);
    if (unserved !== null) parts.push(unserved);
    const stale = levelSetStaleAge(set, body);
    if (stale !== null) parts.push(`STALE ${formatAgeS(stale)}`);
    else if (levelSetStaleReads(set, body).length) parts.push("CLOSED");
  }
  if (prefsError !== null) parts.push("PREFS ERROR");
  return parts.length ? parts.join(" · ") : null;
}

/**
 * HUD-FACE-COHERENCE-1 R8: the WALLS verdict word beside the wall door's AgePill. It reads the wall door alone,
 * and STALE outranks CLOSED, so another feed's closure never masks a stale wall feed (review 01a15a67).
 */
export function sameDoorWallVerdict(feed: {
  stale?: boolean | null; age_s?: number | null; budget_s?: number | null;
  venue_state?: { verdict?: string | null } | null;
} | null | undefined): "STALE" | "CLOSED" | null {
  if (!feed) return null;
  const overBudget = typeof feed.age_s === "number" && typeof feed.budget_s === "number" && feed.age_s > feed.budget_s;
  if (feed.stale === true || overBudget) return "STALE";
  return feed.venue_state?.verdict === "CLOSED" ? "CLOSED" : null;
}
